import { useState } from 'react';
import { AreaChart } from '@/features/ah/charts';
import { fmtNum, fmtTimeNL } from '@/features/ah/utils';
import type { PrijsfavorietenPayload } from '../report';
import { fmtDecimal } from '../utils';

/*
 * 02 — Engagement: the daily-rhythm chart (games or unique visitors, toggled)
 * plus the three per-user stats from the design.
 */
export function EngagementSection({ data }: { data: PrijsfavorietenPayload }) {
  const t = data.totals;

  // Games per day is the headline series (the design's default). The pipeline
  // always emits a per-day curve: from the ledger's own dates when they are
  // usable, otherwise reconstructed from gameplay-event timing and scaled to the
  // ledger's exact per-week totals.
  const [series, setSeries] = useState<'games' | 'visitors'>('games');

  // Averages divide by days that actually have data, not by the whole campaign
  // window — a mid-campaign report would otherwise understate the daily rate by
  // averaging in days that haven't happened yet. Same "active days" convention
  // the Carrefour reports use for avg-daily-visitors.
  const activeDays = Math.max(1, data.series.games.filter((v) => v > 0).length);

  const seriesMap = {
    games: {
      label: 'Games per dag',
      chartLabel: 'Games',
      data: data.series.games,
      color: '#0E4A82',
      total: t.gamesPlayed,
      sub: `Gemiddeld ${fmtNum(Math.round(t.gamesPlayed / activeDays))} per dag.`,
    },
    visitors: {
      label: 'Unieke bezoekers',
      chartLabel: 'Unieke bezoekers',
      data: data.series.visitors,
      color: '#1B6FB8',
      total: t.uniqueVisitors,
      sub: `Gemiddeld ${fmtNum(t.avgVisitorsPerDay)} unieke bezoekers per dag.`,
    },
  } as const;
  const cur = seriesMap[series];
  const days = data.days.map((d) => new Date(d));

  return (
    <section id="engagement" className="section">
      <div className="section-head">
        <div>
          <div className="eyebrow">02 — Engagement</div>
          <h2>Dagelijks ritme</h2>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>{cur.label}</h3>
          <div className="pf-chip-row">
            {Object.entries(seriesMap).map(([k, v]) => (
              <button key={k} type="button" className={'preset-chip' + (series === k ? ' active' : '')} onClick={() => setSeries(k as keyof typeof seriesMap)}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <div className="trend-block">
          <div className="trend-summary">
            <div className="label">Totaal — campagne</div>
            <div className="big">{fmtNum(cur.total)}</div>
            <div className="desc">{cur.sub}</div>
          </div>
          <AreaChart data={[...cur.data]} days={days} color={cur.color} label={cur.chartLabel} height={220} />
        </div>
      </div>

      <div className="card pf-card-gap">
        <div className="big-stat-row">
          <div className="big-stat">
            <div className="label">Games / gebruiker</div>
            <div className="value">{fmtDecimal(t.avgGamesPerPlayer)}</div>
            <div className="desc">Gemiddeld over alle deelnemers</div>
          </div>
          <div className="big-stat">
            <div className="label">Tijd / gebruiker</div>
            <div className="value">{fmtTimeNL(t.avgTimePerUser)}</div>
            <div className="desc">{fmtTimeNL(t.timeInCampaign)} totaal in app</div>
          </div>
          <div className="big-stat">
            <div className="label">Tijd / sessie</div>
            <div className="value">{fmtTimeNL(t.avgSessionDuration)}</div>
            <div className="desc">gem. sessieduur over {fmtNum(t.visits)} sessies</div>
          </div>
        </div>
      </div>
    </section>
  );
}
